/**
 * Staff sign in with a username and a PIN, not an email and password, a
 * cashier should not be typing an address between customers. Supabase Auth
 * only speaks email/password, so the pair is mapped onto a synthetic address
 * and password (identToEmail, staffPassword below).
 *
 * The mapping grants nothing on its own: the password still has to match what
 * was set when the account was created, and every read and write is gated by
 * row-level security.
 */

/**
 * Where a staff member's synthetic address lives now.
 *
 * Under a domain we own. Nothing is ever delivered here, the address exists to
 * fill Supabase Auth's identity field, and accounts are created with
 * email_confirm already set.
 *
 * No tenant in it on purpose: usernames are unique across the whole system
 * now (admins_username_uniq), which is what lets the sign-in screen ask for
 * one field instead of a kurum code and a name.
 */
const STAFF_DOMAIN = 'kullanici.adissoo.com'

/** Whether what someone typed is an email rather than a username. */
export function looksLikeEmail(ident: string): boolean {
  return ident.includes('@')
}

const REMEMBERED = 'adissoo.login'

/**
 * The identity this device signed in with last, offered back on the next
 * visit. Only the name, never the PIN or password, which is the whole point
 * of the box being optional.
 */
export function rememberedLogin(): string {
  try {
    return localStorage.getItem(REMEMBERED) ?? ''
  } catch {
    // Private browsing, or storage turned off. An empty field is the correct
    // fallback; nothing here is worth failing a sign-in over.
    return ''
  }
}

export function rememberLogin(ident: string): void {
  try {
    localStorage.setItem(REMEMBERED, ident)
  } catch {
    // As above.
  }
}

export function forgetLogin(): void {
  try {
    localStorage.removeItem(REMEMBERED)
  } catch {
    // As above.
  }
}

/**
 * The address to sign in with, from whatever was typed.
 *
 * An owner registers with their own email and types that; staff type only the
 * name their owner gave them and never see the address built from it.
 */
export function identToEmail(ident: string): string {
  const cleaned = ident.trim()
  if (looksLikeEmail(cleaned)) return cleaned.toLowerCase()
  return `${normalizeUsername(cleaned)}@${STAFF_DOMAIN}`
}

/** The address a newly added staff member gets. */
export function staffEmail(username: string): string {
  return `${normalizeUsername(username)}@${STAFF_DOMAIN}`
}

/**
 * A staff PIN, stretched into something Supabase will accept as a password.
 *
 * No tenant in the recipe any more, usernames are unique system-wide, so the
 * name alone identifies the account. Salting with the username rather than
 * using the bare PIN keeps two people who both chose 1234 from ending up with
 * the same stored hash.
 *
 * This is obfuscation, not security: the scheme is in the client bundle for
 * anyone to read. What actually protects the account is that the PIN has to
 * match, Supabase rate-limits attempts, and every read and write is gated by
 * RLS on admins.
 */
export function staffPassword(username: string, pin: string): string {
  return `adissoo:${normalizeUsername(username)}:${pin}`
}

export function normalizeUsername(username: string): string {
  return username
    .trim()
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9._-]/g, '')
}

/** The username part of a staff address, dropping any reset suffix. */
export function emailToUsername(email: string): string {
  const local = email.split('@')[0] ?? email
  return local.split('+')[0]
}

/**
 * The institution code suggested from what the business calls itself.
 *
 * "Mercan Restoran" becomes `mercan`, not `mercan-restoran`: this ends up in
 * an address every member of staff types, and the shorter it is the sooner
 * they stop mistyping it. The rest of the name is held in reserve for when
 * the first word is taken.
 *
 * Turkish letters are folded the way usernames already fold them, the same
 * table, so "Köşe" resolves to `kose` here exactly as it would there.
 *
 * Returns candidates in the order they should be tried rather than one
 * answer, because whether a code is free is a question only the server can
 * settle.
 */
export function tenantIdCandidates(displayName: string): string[] {
  // Split first, fold second. normalizeUsername strips whitespace along with
  // everything else it does not keep, so folding the whole name first hands
  // back "mercanrestoran", one word where there were two, and no way left to
  // tell where the first one ended.
  const words = displayName
    // An apostrophe separates, it does not join: Turkish hangs suffixes off
    // one ("Ali'nin"), and the name is the part before it.
    .split(/[\s._'’-]+/)
    .map((w) => normalizeUsername(w.replace(/[^\p{L}\p{N}]/gu, '')))
    .filter(Boolean)

  if (words.length === 0) return []

  const out: string[] = []
  const add = (s: string) => {
    // Two characters is the shortest the server accepts, and a duplicate
    // suggestion would waste one of the attempts.
    if (s.length >= 2 && s.length <= 40 && !out.includes(s)) out.push(s)
  }

  add(words[0])
  // The first two words joined, which is what distinguishes "Köşe Kahvaltı"
  // from every other "Köşe" before a bare number has to.
  if (words.length > 1) add(`${words[0]}-${words[1]}`)
  add(words.join('-').slice(0, 40))
  // Numbered last: "mercan-2" says nothing about the business, so it is where
  // the useful names run out rather than the first thing offered.
  // Only once there is a real name to number. "a-2" passes the server's
  // length check while saying nothing at all, so a name too short to use is
  // better answered with nothing, the field then asks for a longer one.
  if (words[0].length >= 2) {
    for (let n = 2; n <= 9; n++) add(`${words[0]}-${n}`)
  }

  return out
}
